import {
  alpha,
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import { createContext, useEffect, useMemo, useState } from "react";

// Create the context
export const ThemeContext = createContext();

/**
 * Component overrides shared by both modes.
 *
 * The look here follows mui.com rather than MUI's stock defaults: surfaces are
 * flat and separated by hairline borders instead of elevation shadows, and the
 * app bar is a translucent, blurred panel rather than a solid primary slab.
 */
const sharedComponents = {
  // Theme the scrollbar so it tracks the palette instead of falling back to
  // the browser's light-grey default (which reads as white on dark surfaces).
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      "*": {
        scrollbarWidth: "thin", // Firefox
        scrollbarColor: `${theme.palette.divider} transparent`,
      },
      "*::-webkit-scrollbar": {
        width: 10,
        height: 10,
      },
      "*::-webkit-scrollbar-track": {
        backgroundColor: theme.palette.background.default,
      },
      "*::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.divider,
        borderRadius: 8,
        border: `2px solid ${theme.palette.background.default}`,
      },
      "*::-webkit-scrollbar-thumb:hover": {
        backgroundColor: theme.palette.action.disabled,
      },
    }),
  },

  MuiAppBar: {
    // Stock AppBar is primary.main + elevation 4; both are dropped here.
    defaultProps: {
      elevation: 0,
      color: "inherit",
    },
    styleOverrides: {
      root: ({ theme }) => ({
        border: "none",
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.background.default, 0.72),
        // Falls back to the (opaque) backgroundColor where unsupported.
        backdropFilter: "blur(20px)",
      }),
    },
  },

  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      // Only flat surfaces get a hairline border. Raised Papers (menus,
      // dialogs, popovers) keep their shadow and stay borderless.
      root: ({ theme, ownerState }) => ({
        backgroundImage: "none", // kill the dark-mode elevation overlay
        ...(ownerState.elevation === 0 &&
          ownerState.variant !== "outlined" && {
            border: `1px solid ${theme.palette.divider}`,
          }),
      }),
    },
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        textTransform: "none",
        fontWeight: 600,
      },
    },
  },

  // --- Form inputs -------------------------------------------------------
  // Small + outlined everywhere, matching the admin pages' search fields.

  MuiTextField: {
    defaultProps: {
      variant: "outlined",
      size: "small",
    },
    styleOverrides: {
      root: ({ theme }) => ({
        "& .MuiOutlinedInput-root": {
          backgroundColor: theme.palette.background.paper,
        },
      }),
    },
  },

  MuiSelect: {
    defaultProps: {
      size: "small",
    },
  },

  MuiFormControl: {
    defaultProps: {
      size: "small",
    },
  },

  MuiAutocomplete: {
    defaultProps: {
      size: "small",
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
      }),
    },
  },

  // --- Tables ------------------------------------------------------------
  // Bordered container + sticky bold header, per the admin management pages.

  MuiTableContainer: {
    styleOverrides: {
      // `component={Paper}` is common here; MuiPaper's own border is scoped to
      // skip this class so the two don't stack into a double border.
      root: ({ theme }) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
      }),
    },
  },

  MuiTableHead: {
    styleOverrides: {
      root: ({ theme }) => ({
        "& th": {
          fontWeight: "bold",
          backgroundColor: theme.palette.background.default,
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
      }),
    },
  },

  MuiTableRow: {
    styleOverrides: {
      // Hover is a body-row affordance only; header rows keep their own
      // background.
      root: ({ theme }) => ({
        "&:not(.MuiTableRow-head):hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&:last-child td, &:last-child th": {
          border: 0,
        },
      }),
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
    },
  },

  // --- Overlays ----------------------------------------------------------

  MuiDialog: {
    defaultProps: {
      // Matches the blur used on the app bar.
      BackdropProps: { sx: { backdropFilter: "blur(3px)" } },
    },
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        backgroundImage: "none",
      }),
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontWeight: 600,
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        backgroundImage: "none",
      }),
    },
  },

  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      // Background lives on the `tooltip` slot; the arrow is a separate slot
      // and must be colored to match or it stays the default grey.
      tooltip: ({ theme }) => ({
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
        backgroundColor: theme.palette.tooltip.bg,
        color: theme.palette.tooltip.color,
        fontSize: "0.75rem",
        "& ::-webkit-scrollbar": {
          width: "6px",
        },
        "& ::-webkit-scrollbar-thumb": {
          bgcolor: theme.palette.border.secondary,
          borderRadius: "4px",
        },
        "& ::-webkit-scrollbar-track": {
          //bgcolor: theme.palette.action.hover,
        },
        // Standard (Firefox)
        scrollbarColor: `${theme.palette.border.secondary} transparent`,
        scrollbarWidth: "thin",
      }),
      arrow: ({ theme }) => ({
        color: theme.palette.tooltip.bg,
      }),
    },
  },
};

const sharedTheme = {
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  components: sharedComponents,
};

const lightTheme = createTheme({
  ...sharedTheme,
  palette: {
    mode: "light",
    primary: {
      main: "#9f86ff",
      dark: "#8c6efd",
      light: "#ab97fb",
      contrastText: "#e8e8ec",
    },
    background: {
      default: "#f7f7f8",
      paper: "#FFFFFF",
    },
    action: {
      hover: "#ededf0",
      selected: "#ddf4ff",
    },
    // Custom key — read by the MuiTooltip override above.
    tooltip: {
      bg: "#f3f3f6",
      color: "#1f2328",
    },
    border: { primary: "#e3e3e8", secondary: "#E7EBF0" },
    text: {
      primary: "#1a1a1e",
      secondary: "#6b6b76",
    },
    divider: "#e3e3e8",
  },
});

const darkTheme = createTheme({
  ...sharedTheme,
  palette: {
    mode: "dark",
    primary: {
      main: "#9f86ff",
      dark: "#8c6efd",
      light: "#ab97fb",
      contrastText: "#e8e8ec",
    },
    background: {
      default: "#18171d",
      paper: "#28272d",
    },
    action: {
      hover: "#3a383f",
    },
    // Custom key — read by the MuiTooltip override above.
    tooltip: {
      bg: "#131316",
      color: "#eceff1",
    },
    border: { primary: "#4c4b51", secondary: "#2A3542" },
    text: {
      primary: "#e8e8ec",
      secondary: "#9a9aa5",
    },
    divider: "#4c4b51",
  },
});

export const ThemeProvider = ({ children }) => {
  // Check if there's a saved theme preference in localStorage
  const [themeMode, setThemeMode] = useState(() => {
    const savedTheme = localStorage.getItem("themeMode");
    // Default to dark theme if no preference is saved
    return savedTheme === "light" ? "light" : "dark";
  });

  // Save theme preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("themeMode", themeMode);
  }, [themeMode]);

  // Toggle between light and dark themes
  const toggleTheme = () => {
    setThemeMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  // Create the current theme based on the mode
  const theme = useMemo(() => {
    return themeMode === "light" ? lightTheme : darkTheme;
  }, [themeMode]);

  // Provide the theme and toggle function to the context
  const contextValue = {
    themeMode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
